import api from './api';
import { Email, SmtpSettings, EmailFormData, EmailFilters, EmailStats, SmtpFormData, Lead } from '../types/Email';

export const emailApi = {
  // Global Email Setting (for document module)
  getGlobalEmailSetting: async (): Promise<{ useGlobalEmailSetting: boolean }> => {
    const response = await api.get('/emails/global-email-setting');
    return response.data.data;
  },

  updateGlobalEmailSetting: async (useGlobalEmailSetting: boolean): Promise<{ useGlobalEmailSetting: boolean }> => {
    const response = await api.put('/emails/global-email-setting', { useGlobalEmailSetting });
    return response.data.data;
  },

  // SMTP Settings
  getSmtpSettings: async (): Promise<SmtpSettings[]> => {
    const response = await api.get('/emails/smtp-settings');
    return response.data.data;
  },

  getActiveSmtpSettings: async (): Promise<SmtpSettings | null> => {
    const response = await api.get('/emails/smtp-settings/active');
    return response.data.data;
  },

  createSmtpSettings: async (data: SmtpFormData): Promise<SmtpSettings> => {
    const response = await api.post('/emails/smtp-settings', data);
    return response.data.data;
  },

  updateSmtpSettings: async (id: number, data: SmtpFormData): Promise<SmtpSettings> => {
    const response = await api.put(`/emails/smtp-settings/${id}`, data);
    return response.data.data;
  },

  deleteSmtpSettings: async (id: number): Promise<void> => {
    await api.delete(`/emails/smtp-settings/${id}`);
  },

  testSmtpConnection: async (data: { smtpHost: string; smtpPort: number; smtpSecure: boolean; smtpUsername: string; smtpPassword: string }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/emails/smtp-settings/test', data);
    return response.data;
  },

  // Emails
  getEmails: async (filters: EmailFilters): Promise<{ emails: Email[]; pagination: any }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/emails?${params.toString()}`);
    return {
      emails: response.data.data,
      pagination: response.data.pagination
    };
  },

  getEmail: async (id: number): Promise<Email> => {
    const response = await api.get(`/emails/${id}`);
    return response.data.data;
  },

  sendEmail: async (emailData: EmailFormData, saveAsDraft: boolean = false): Promise<Email> => {
    const formData = new FormData();
    
    formData.append('subject', emailData.subject);
    formData.append('body', emailData.body);
    if (emailData.htmlBody) {
      formData.append('htmlBody', emailData.htmlBody);
    }
    formData.append('toEmails', JSON.stringify(emailData.toEmails));
    formData.append('ccEmails', JSON.stringify(emailData.ccEmails));
    formData.append('bccEmails', JSON.stringify(emailData.bccEmails));
    formData.append('saveAsDraft', saveAsDraft.toString());

    // Add attachments
    emailData.attachments.forEach((file, index) => {
      formData.append('attachments', file);
    });

    const response = await api.post('/emails/send', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  updateEmail: async (id: number, emailData: Partial<EmailFormData>): Promise<Email> => {
    const response = await api.put(`/emails/${id}`, emailData);
    return response.data.data;
  },

  deleteEmail: async (id: number): Promise<void> => {
    await api.delete(`/emails/${id}`);
  },

  sendDraft: async (id: number): Promise<Email> => {
    const response = await api.put(`/emails/${id}/send`);
    return response.data.data;
  },

  getEmailStats: async (): Promise<EmailStats> => {
    const response = await api.get('/emails/stats/overview');
    return response.data.data;
  },

  // Leads for email composition
  getLeadsForEmail: async (): Promise<Lead[]> => {
    const response = await api.get('/leads');
    return response.data.data;
  },

  // Utility functions
  formatFileSize: (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  validateEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  parseEmailList: (emailString: string): string[] => {
    return emailString
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
  },

  formatEmailList: (emails: string[]): string => {
    return emails.join(', ');
  }
};

export default emailApi;
